import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Divider,
} from "@mui/material";
import { Icon } from "@iconify/react";

function ImportResults({ results, onFinish }) {
  if (!results) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Alert severity="error">
          <Typography variant="body2">
            Keine Import-Ergebnisse verfügbar.
          </Typography>
        </Alert>
      </Box>
    );
  }

  const { success, summary, errors } = results;

  return (
    <Box sx={{ textAlign: "center" }}>
      {success ? (
        <>
          <Icon icon="mdi:check-circle" width={80} style={{ color: "#2e7d32" }} />
          <Typography variant="h4" gutterBottom sx={{ mt: 2, fontWeight: "bold", color: "success.main" }}>
            Import erfolgreich abgeschlossen!
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Ihre Daten wurden erfolgreich importiert und sind jetzt in Ihrem System verfügbar.
          </Typography>
        </>
      ) : (
        <>
          <Icon icon="mdi:alert-circle" width={80} style={{ color: "#d32f2f" }} />
          <Typography variant="h4" gutterBottom sx={{ mt: 2, fontWeight: "bold", color: "error.main" }}>
            Import teilweise fehlgeschlagen
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Einige Daten konnten nicht importiert werden. Details finden Sie unten.
          </Typography>
        </>
      )}

      <Paper elevation={2} sx={{ p: 3, mt: 4, textAlign: "left" }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Import-Statistik
        </Typography>
        <List>
          {summary?.sites > 0 && (
            <ListItem>
              <ListItemIcon>
                <Icon icon="mdi:map-marker" width={24} style={{ color: "#2e7d32" }} />
              </ListItemIcon>
              <ListItemText
                primary={`${summary.sites} Standorte`}
                secondary="erfolgreich importiert"
              />
            </ListItem>
          )}
          {summary?.members > 0 && (
            <ListItem>
              <ListItemIcon>
                <Icon icon="mdi:account-group" width={24} style={{ color: "#2e7d32" }} />
              </ListItemIcon>
              <ListItemText
                primary={`${summary.members} Mitglieder`}
                secondary="erfolgreich importiert"
              />
            </ListItem>
          )}
          {summary?.contracts > 0 && (
            <ListItem>
              <ListItemIcon>
                <Icon icon="mdi:file-document" width={24} style={{ color: "#2e7d32" }} />
              </ListItemIcon>
              <ListItemText
                primary={`${summary.contracts} Verträge`}
                secondary="erfolgreich erstellt"
              />
            </ListItem>
          )}
          {summary?.events > 0 && (
            <ListItem>
              <ListItemIcon>
                <Icon icon="mdi:calendar" width={24} style={{ color: "#2e7d32" }} />
              </ListItemIcon>
              <ListItemText
                primary={`${summary.events} Veranstaltungen`}
                secondary="erfolgreich importiert"
              />
            </ListItem>
          )}
          {summary?.bookings > 0 && (
            <ListItem>
              <ListItemIcon>
                <Icon icon="mdi:cash-register" width={24} style={{ color: "#2e7d32" }} />
              </ListItemIcon>
              <ListItemText
                primary={`${summary.bookings} Buchungen`}
                secondary="erfolgreich importiert"
              />
            </ListItem>
          )}
          {summary?.siteManagers > 0 && (
            <ListItem>
              <ListItemIcon>
                <Icon icon="mdi:account-supervisor" width={24} style={{ color: "#2e7d32" }} />
              </ListItemIcon>
              <ListItemText
                primary={`${summary.siteManagers} Standortleiter`}
                secondary="erfolgreich zugeordnet"
              />
            </ListItem>
          )}
        </List>
      </Paper>

      {errors && errors.length > 0 && (
        <Paper elevation={1} sx={{ p: 3, mt: 3, textAlign: "left", bgcolor: "#fee" }}>
          <Typography variant="h6" gutterBottom fontWeight="bold" color="error">
            Fehler beim Import ({errors.length})
          </Typography>
          <Divider sx={{ my: 2 }} />
          <List dense>
            {errors.map((error, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <Icon icon="mdi:alert-circle" width={20} style={{ color: "#d32f2f" }} />
                </ListItemIcon>
                <ListItemText
                  primary={error.message}
                  secondary={error.details || "Weitere Details nicht verfügbar"}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Nächste Schritte:</strong><br />
          • Überprüfen Sie Ihre importierten Daten in den jeweiligen Bereichen<br />
          • Vervollständigen Sie fehlende Informationen falls nötig<br />
          • Bei Problemen kontaktieren Sie bitte den Support
        </Typography>
      </Alert>

      <Box sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "center" }}>
        <Button
          variant="outlined"
          size="large"
          onClick={() => window.open("/members", "_blank")}
          startIcon={<Icon icon="mdi:account-group" />}
        >
          Mitglieder anzeigen
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={onFinish}
          startIcon={<Icon icon="mdi:check" />}
        >
          Fertig
        </Button>
      </Box>
    </Box>
  );
}

ImportResults.propTypes = {
  results: PropTypes.object,
  onFinish: PropTypes.func.isRequired,
};

export default ImportResults;
